import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { CONTACT } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { submitEnquiry } from "../lib/api";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete all fields", description: "We need your name, email and a short message." });
      return;
    }
    if (!form.email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
      toast({ title: "That email doesn't look right", description: "Please double-check and try again." });
      return;
    }
    setBusy(true);
    try {
      await submitEnquiry({
        kind: "contact",
        email: form.email,
        name: form.name,
        message: form.message
      });
      toast({ title: "Thanks for your message!", description: "We'll get back to you shortly." });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or email us directly." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-[11px] tracking-[0.3em] uppercase text-forge-gold">Contact Us</div>
          <h2 className="font-serif text-4xl md:text-5xl text-forge-navy mt-2">Get in Touch</h2>
          <hr className="forge-divider" />
          <p className="text-forge-navy/70">Questions about Forge Stays? We'd love to hear from you.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-10">
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-forge-cream flex items-center justify-center shrink-0">
                <Mail size={18} className="text-forge-navy" />
              </div>
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-forge-navy/60">Email</div>
                <a href={`mailto:${CONTACT.email}`} className="text-forge-navy hover:text-forge-gold">{CONTACT.email}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-forge-cream flex items-center justify-center shrink-0">
                <Phone size={18} className="text-forge-navy" />
              </div>
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-forge-navy/60">Phone</div>
                <a href={`tel:${CONTACT.phoneRaw}`} className="text-forge-navy hover:text-forge-gold">{CONTACT.phone}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-forge-cream flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-forge-navy" />
              </div>
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-forge-navy/60">Address</div>
                <div className="text-forge-navy">{CONTACT.address_line1}</div>
                <div className="text-forge-navy/70 text-sm">{CONTACT.address_line2}</div>
                <div className="text-forge-navy/70 text-sm">{CONTACT.address_line3}</div>
              </div>
            </div>
            <div className="pt-2 flex flex-wrap gap-3">
              <a href={`mailto:${CONTACT.email}?subject=Enquiry%20about%20Forge%20Stays`} className="btn-navy">
                <Send size={14} /> Send Email
              </a>
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-[#25D366] text-white uppercase tracking-[0.16em] text-xs font-semibold hover:brightness-95 transition">
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>
          </div>

          <form onSubmit={submit} className="bg-forge-off p-8 border border-black/5 space-y-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-forge-navy/60">Your Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="mt-2 w-full bg-white px-4 py-3 border border-black/10 focus:border-[var(--forge-gold)] outline-none text-forge-navy" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-forge-navy/60">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="mt-2 w-full bg-white px-4 py-3 border border-black/10 focus:border-[var(--forge-gold)] outline-none text-forge-navy" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-forge-navy/60">Message</label>
              <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="mt-2 w-full bg-white px-4 py-3 border border-black/10 focus:border-[var(--forge-gold)] outline-none text-forge-navy" />
            </div>
            <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-70">
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              {busy ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
